import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { DocumentText1, Edit2, Profile, Moneys, Calendar, Location, ImportCurve } from "iconsax-react";
import cn from "classnames";
import { baseApi } from "@/shared/api";
import { useAuth, useAppDispatch } from "@/shared/hooks";
import { formatDateForDisplay, formatMoneyKzt, extractErrorMessage, downloadBlob } from "@/shared/utils";
import { ModalForm, Badge, Skeleton, FileViewer, Prompt, PromptForm, Button, ToggleButton } from "@/shared/ui";
import { ReviewNoteModal } from "@/features/hr/shared";
import { useOrderSigning } from "@/features/signing";
import {
  useGetAdvanceDetailQuery,
  useGetBusinessTripDetailQuery,
  useIssueAdvanceMutation,
  useIssueBusinessTripMutation,
  useDownloadAdvanceT3Mutation,
  useDownloadBusinessTripT3Mutation,
  useReviewAdvanceMutation,
  useReviewBusinessTripMutation,
  usePreviewBusinessTripOrderMutation,
  useSignBusinessTripOrderMutation,
} from "../../api";
import { ADVANCE_STATUS_MAP } from "../../consts/statuses";
import { getAvailableActions } from "../../utils/advanceRules";
import type { ProjectAllocationItem } from "../../types";
import { AdvanceSettlementForm, TripSettlementForm, ReportLinesSection, SalaryAdvanceIssueModal } from "./components";

const OrgSignByDocumentModal = lazy(() =>
  import("@/features/self/pending/components/OrgSignByDocumentModal").then((m) => ({
    default: m.default,
  })),
);

interface Props {
  entityId: number;
  entityType: "advance" | "trip";
  onClose: () => void;
  onActionSuccess?: () => void;
  autoOpenSettlement?: boolean;
}

interface PromptState {
  title: string;
  text: string;
  variant?: "success" | "error" | "warning";
}

interface FieldConfig {
  key: string;
  label: string;
  value: string;
  icon: typeof DocumentText1;
  isStatus?: boolean;
  statusComponent?: React.ReactNode;
}

function filterField(field: FieldConfig): boolean {
  return !!(field.value || field.isStatus);
}

function renderField(field: FieldConfig, index: number, array: FieldConfig[]) {
  const IconComponent = field.icon;
  return (
    <div
      key={field.key}
      className={cn("py-3 flex items-center gap-3", index < array.length - 1 && "border-b surface-base-stroke")}>
      <span className="content-action-brand">
        <IconComponent size={16} color="currentColor" />
      </span>
      <span className="text-body-regular-md content-base-secondary flex-1 min-w-[108px]">{field.label}</span>
      {field.isStatus ? (
        <div className="flex justify-end">{field.statusComponent}</div>
      ) : (
        <p className="text-body-bold-md content-base-primary text-right">{field.value}</p>
      )}
    </div>
  );
}

export default function AdvanceDetailsModal({
  entityId,
  entityType,
  onClose,
  onActionSuccess,
  autoOpenSettlement,
}: Props) {
  const { t } = useTranslation("AdvancesPage");
  const { t: tCommon } = useTranslation("Common");
  const { hasRole } = useAuth();
  const dispatch = useAppDispatch();

  const advanceQuery = useGetAdvanceDetailQuery(entityId, { skip: entityType !== "advance" });
  const tripQuery = useGetBusinessTripDetailQuery(entityId, { skip: entityType !== "trip" });

  const isLoading = entityType === "advance" ? advanceQuery.isLoading : tripQuery.isLoading;
  const isError = entityType === "advance" ? advanceQuery.isError : tripQuery.isError;
  const advanceData = entityType === "advance" ? advanceQuery.data : undefined;
  const tripData = entityType === "trip" ? tripQuery.data : undefined;

  const [prompt, setPrompt] = useState<PromptState | null>(null);
  const [confirmIssue, setConfirmIssue] = useState(false);
  const [issuingLocal, setIssuingLocal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isReviewing, setIsReviewing] = useState(false);
  const [showRevisionNoteModal, setShowRevisionNoteModal] = useState(false);
  const [showRejectNoteModal, setShowRejectNoteModal] = useState(false);
  const [showOrgSign, setShowOrgSign] = useState(false);
  const [showSettlement, setShowSettlement] = useState(false);
  const [showSalaryIssueModal, setShowSalaryIssueModal] = useState(false);
  const [fromCashDesk, setFromCashDesk] = useState(false);

  const [issueAdvance] = useIssueAdvanceMutation();
  const [issueBusinessTrip] = useIssueBusinessTripMutation();
  const [downloadAdvanceT3] = useDownloadAdvanceT3Mutation();
  const [downloadBusinessTripT3] = useDownloadBusinessTripT3Mutation();
  const [reviewAdvance] = useReviewAdvanceMutation();
  const [reviewBusinessTrip] = useReviewBusinessTripMutation();
  const [previewTripOrder] = usePreviewBusinessTripOrderMutation();
  const [signTripOrder] = useSignBusinessTripOrderMutation();

  const canIssue = hasRole("accountant") || hasRole("chief");
  const isChief = hasRole("chief");

  const autoSettleHandledRef = useRef(false);
  useEffect(
    function autoOpenSettlementOnce() {
      if (!autoOpenSettlement || autoSettleHandledRef.current || isLoading) return;
      const hasData = entityType === "advance" ? !!advanceData : !!tripData;
      if (!hasData) return;
      autoSettleHandledRef.current = true;
      setShowSettlement(true);
    },
    [autoOpenSettlement, isLoading, advanceData, tripData, entityType],
  );

  const tripSigning = useOrderSigning({
    previewFn() {
      return previewTripOrder(entityId)
        .unwrap()
        .then((r) => r.blob);
    },
    signFn(cmsSignature) {
      return signTripOrder({ tripId: entityId, cms_signature: cmsSignature }).unwrap();
    },
    skipFileUpload: true,
    onSuccess() {
      setPrompt({
        title: t("messages.reviewSuccessTitle"),
        text: t("messages.tripSignSuccessText"),
        variant: "success",
      });
    },
    setPrompt,
    t: tCommon,
  });

  function handlePromptClose() {
    const wasSuccess = prompt?.variant === "success";
    setPrompt(null);
    if (wasSuccess) {
      onActionSuccess?.();
    }
  }

  async function handleIssueConfirm(dto?: { from_cash_desk: boolean; project_allocations: ProjectAllocationItem[] }) {
    setConfirmIssue(false);
    setShowSalaryIssueModal(false);
    if (issuingLocal) return;
    try {
      setIssuingLocal(true);
      if (entityType === "trip") {
        await issueBusinessTrip(entityId).unwrap();
      } else {
        await issueAdvance({
          id: entityId,
          from_cash_desk: dto?.from_cash_desk ?? fromCashDesk,
          project_allocations: dto?.project_allocations,
        }).unwrap();
      }
      await Promise.all(dispatch(baseApi.util.getRunningQueriesThunk()));
      setPrompt({
        title: t("messages.issueSuccessTitle"),
        text: t("messages.issueSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.issueError", t),
        variant: "error",
      });
    } finally {
      setIssuingLocal(false);
    }
  }

  async function handleDownloadT3() {
    if (isDownloading) return;
    try {
      setIsDownloading(true);
      let result;
      if (entityType === "trip") {
        result = await downloadBusinessTripT3(entityId).unwrap();
      } else {
        result = await downloadAdvanceT3(entityId).unwrap();
      }
      downloadBlob(result.blob, result.filename || t("messages.t3DefaultFilename"));
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.t3Error", t),
        variant: "error",
      });
    } finally {
      setIsDownloading(false);
    }
  }

  async function handleApprove() {
    if (entityType === "trip") {
      if (tripSigning.isLoading) return;
      tripSigning.handleSign();
      return;
    }
    if (isReviewing) return;
    try {
      setIsReviewing(true);
      await reviewAdvance({ id: entityId, action: "approve" }).unwrap();
      setPrompt({
        title: t("messages.reviewSuccessTitle"),
        text: t("messages.reviewApproveSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.reviewError", t),
        variant: "error",
      });
    } finally {
      setIsReviewing(false);
    }
  }

  async function handleRevisionWithNote(note: string) {
    if (isReviewing) return;
    setShowRevisionNoteModal(false);
    try {
      setIsReviewing(true);
      if (entityType === "trip") {
        await reviewBusinessTrip({ id: entityId, action: "revision", note }).unwrap();
      } else {
        await reviewAdvance({ id: entityId, action: "revision", note }).unwrap();
      }
      setPrompt({
        title: t("messages.reviewSuccessTitle"),
        text: t("messages.reviewRevisionSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.reviewError", t),
        variant: "error",
      });
    } finally {
      setIsReviewing(false);
    }
  }

  async function handleRejectWithNote(note: string) {
    if (isReviewing) return;
    setShowRejectNoteModal(false);
    try {
      setIsReviewing(true);
      if (entityType === "trip") {
        await reviewBusinessTrip({ id: entityId, action: "reject", note }).unwrap();
      } else {
        await reviewAdvance({ id: entityId, action: "reject", note }).unwrap();
      }
      setPrompt({
        title: t("messages.reviewSuccessTitle"),
        text: t("messages.reviewRejectSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.reviewError", t),
        variant: "error",
      });
    } finally {
      setIsReviewing(false);
    }
  }

  function getStatusBadge(status: import("../../types").AdvanceStatus) {
    const config = ADVANCE_STATUS_MAP[status] ?? {
      color: "gray" as const,
      label: `statusLabels.${status}`,
      icon: <DocumentText1 size={14} color="currentColor" />,
    };
    return <Badge variant="soft" color={config.color} text={t(config.label)} icon={config.icon} />;
  }

  function getIssueConfirmText(): string {
    if (entityType === "trip" && tripData) {
      const perDiem = (Number(tripData.per_diem_rate) || 0) * tripData.days;
      const advance = Number(tripData.advance_amount) || 0;
      return t("actions.issueTripConfirmText", {
        perDiem: formatMoneyKzt(String(perDiem)),
        advance: formatMoneyKzt(String(advance)),
        total: formatMoneyKzt(String(perDiem + advance)),
      });
    }
    return t("actions.issueConfirmText");
  }

  function renderFooter() {
    const status = advanceData?.status ?? tripData?.status;
    const reviewNote = advanceData?.application_review_note ?? tripData?.application_review_note ?? null;
    const kind = advanceData?.kind;

    if (isLoading || (!advanceData && !tripData) || !status) return undefined;

    const entityForRules = {
      status,
      type: entityType as import("../../types").AdvanceListType,
      kind,
      application_review_note: reviewNote,
    };

    const loadingState = {
      isIssuing: issuingLocal,
      isReviewing,
      isDownloadingT3: isDownloading,
    };

    const { actions: allActions } = getAvailableActions(entityForRules, loadingState, t);

    const seen = new Set<string>();
    const visibleActions = allActions.filter(function filterAndDedup(a) {
      if (seen.has(a.id)) return false;
      const reviewIds = ["approve", "request_revision", "reject"] as const;
      const issueIds = ["issue", "settle", "download_t3"] as const;
      const isReviewAction = (reviewIds as readonly string[]).includes(a.id);
      const isIssueAction = (issueIds as readonly string[]).includes(a.id);
      if (isReviewAction && !isChief) return false;
      if (isIssueAction && !canIssue) return false;
      if (a.id === "sign" && !isChief) return false;
      if (a.id === "sign" && !advanceData?.signing_document?.document_id) return false;
      seen.add(a.id);
      return true;
    });

    if (visibleActions.length === 0) return undefined;

    const buttonSize: "lg" | "huge" = visibleActions.length >= 4 ? "lg" : "huge";

    function renderActionButton(action: (typeof visibleActions)[number]) {
      if (action.id === "approve") {
        return (
          <Button
            key="approve"
            variant="primary"
            size={buttonSize}
            onClick={handleApprove}
            disabled={isReviewing || tripSigning.isLoading}>
            {isReviewing
              ? t("messages.loading")
              : entityType === "trip"
                ? t("actions.approveAndSign")
                : action.label}
          </Button>
        );
      }
      if (action.id === "request_revision") {
        return (
          <Button
            key="request_revision"
            variant="secondary"
            size={buttonSize}
            onClick={() => setShowRevisionNoteModal(true)}
            disabled={isReviewing}>
            {action.label}
          </Button>
        );
      }
      if (action.id === "reject") {
        return (
          <Button
            key="reject"
            variant="danger"
            size={buttonSize}
            onClick={() => setShowRejectNoteModal(true)}
            disabled={isReviewing}>
            {action.label}
          </Button>
        );
      }
      if (action.id === "issue") {
        return (
          <Button
            key="issue"
            variant="primary"
            size={buttonSize}
            onClick={() => {
              if (entityType === "advance" && advanceData?.kind === "salary") {
                setShowSalaryIssueModal(true);
              } else {
                setConfirmIssue(true);
              }
            }}
            disabled={issuingLocal}>
            {issuingLocal ? t("messages.loading") : action.label}
          </Button>
        );
      }
      if (action.id === "settle") {
        return (
          <Button key="settle" variant="primary" size={buttonSize} onClick={() => setShowSettlement(true)}>
            {action.label}
          </Button>
        );
      }
      if (action.id === "download_t3") {
        return (
          <Button
            key="download_t3"
            variant="secondary"
            size={buttonSize}
            onClick={handleDownloadT3}
            disabled={isDownloading}>
            {isDownloading ? (
              t("messages.loading")
            ) : (
              <>
                <ImportCurve size={20} color="currentColor" />
                {action.label}
              </>
            )}
          </Button>
        );
      }
      if (action.id === "sign") {
        return (
          <Button
            key="sign"
            variant="primary"
            size={buttonSize}
            onClick={() => setShowOrgSign(true)}>
            {action.label}
          </Button>
        );
      }
      return null;
    }

    if (visibleActions.length === 3) {
      const [primary, ...secondary] = visibleActions;
      return (
        <div className="flex flex-col gap-3">
          {renderActionButton(primary)}
          <div className="grid grid-cols-2 gap-3">
            {secondary.map(renderActionButton)}
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-3">
        {visibleActions.map(renderActionButton)}
      </div>
    );
  }

  function renderAdvanceContent() {
    if (!advanceData) return null;

    const fields: FieldConfig[] = [
      {
        key: "worker",
        label: t("detail.worker"),
        value: advanceData.worker_name,
        icon: Profile as typeof DocumentText1,
      },
      {
        key: "type",
        label: t("detail.type"),
        value: advanceData.kind === "salary" ? t("table.typeLabels.salary") : t("table.typeLabels.employee"),
        icon: DocumentText1,
      },
      {
        key: "amount",
        label: t("detail.amount"),
        value: formatMoneyKzt(advanceData.amount),
        icon: Moneys as typeof DocumentText1,
      },
    ];

    if (advanceData.amount_mode === "percent" && advanceData.percent) {
      fields.push({
        key: "percent",
        label: t("detail.percent"),
        value: `${advanceData.percent}%`,
        icon: Moneys as typeof DocumentText1,
      });
    }

    if (advanceData.computed_net) {
      fields.push({
        key: "computedNet",
        label: t("detail.computedNet"),
        value: formatMoneyKzt(advanceData.computed_net),
        icon: Moneys as typeof DocumentText1,
      });
    }

    if (advanceData.advance_month && advanceData.advance_year) {
      fields.push({
        key: "period",
        label: t("detail.period"),
        value: `${tCommon(`months.${advanceData.advance_month}`)} ${advanceData.advance_year}`,
        icon: Calendar as typeof DocumentText1,
      });
    }

    if (advanceData.document_number) {
      fields.push({
        key: "docNumber",
        label: t("detail.docNumber"),
        value: advanceData.document_number,
        icon: DocumentText1,
      });
    }

    if (advanceData.document_date) {
      fields.push({
        key: "docDate",
        label: t("detail.docDate"),
        value: formatDateForDisplay(advanceData.document_date),
        icon: Calendar as typeof DocumentText1,
      });
    }

    if (advanceData.purpose) {
      fields.push({
        key: "purpose",
        label: t("detail.purpose"),
        value: advanceData.purpose,
        icon: DocumentText1,
      });
    }

    if (advanceData.comment) {
      fields.push({
        key: "comment",
        label: t("detail.comment"),
        value: advanceData.comment,
        icon: DocumentText1,
      });
    }

    if (advanceData.application_review_note) {
      fields.push({
        key: "revisionNote",
        label: t("detail.revisionNote"),
        value: advanceData.application_review_note,
        icon: Edit2 as typeof DocumentText1,
      });
    }

    if (advanceData.report_date) {
      fields.push({
        key: "reportDate",
        label: t("detail.reportDate"),
        value: formatDateForDisplay(advanceData.report_date),
        icon: Calendar as typeof DocumentText1,
      });
    }

    if (advanceData.settled_by_name) {
      fields.push({
        key: "settledBy",
        label: t("detail.settledBy"),
        value: advanceData.settled_by_name,
        icon: Profile as typeof DocumentText1,
      });
    }

    fields.push({
      key: "status",
      label: t("detail.status"),
      value: "",
      icon: DocumentText1,
      isStatus: true,
      statusComponent: getStatusBadge(advanceData.status),
    });

    const visibleFields = fields.filter(filterField);

    return (
      <div className="flex flex-col gap-6 h-full min-h-0">
        <div className="flex flex-col gap-2 pb-5 border-b surface-base-stroke shrink-0">
          <p className="text-display-2xs content-base-primary">{t("detail.title")}</p>
        </div>
        <div className="flex-1 overflow-auto">
          <div className="flex flex-col pt-2">
            {visibleFields.map((field, index, array) => renderField(field, index, array))}
          </div>

          {advanceData.report_lines && advanceData.report_lines.length > 0 && (
            <ReportLinesSection lines={advanceData.report_lines} />
          )}

          {canIssue && advanceData.status === "approved" && (
            <div className="py-3 flex items-center justify-between gap-3 border-t surface-base-stroke mt-2">
              <div className="flex flex-col gap-0.5">
                <span className="text-body-bold-sm content-base-primary">{t("detail.fromCashDesk")}</span>
                <span className="text-body-regular-sm content-base-secondary">{t("detail.fromCashDeskHint")}</span>
              </div>
              <ToggleButton checked={fromCashDesk} onChange={setFromCashDesk} />
            </div>
          )}

          {advanceData.attachments && advanceData.attachments.length > 0 && (
            <div className="py-3 flex flex-col gap-3 border-t surface-base-stroke mt-2">
              <span className="text-body-regular-md content-base-secondary">{t("detail.attachments")}</span>
              {advanceData.attachments.map(function renderAttachment(att) {
                return (
                  <div key={att.id} className="p-1">
                    <FileViewer existingFileUrl={att.file_url} />
                  </div>
                );
              })}
            </div>
          )}

          {advanceData.signing_document &&
            (advanceData.signing_document.signed_pdf_url || advanceData.signing_document.original_file_url) && (
              <div className="py-3 flex flex-col gap-3 border-t surface-base-stroke">
                <span className="text-body-regular-md content-base-secondary">{t("detail.signingDocumentLabel")}</span>
                <div className="p-1">
                  <FileViewer
                    existingFileUrl={
                      (advanceData.signing_document.signed_pdf_url ||
                        advanceData.signing_document.original_file_url) as string
                    }
                    secondaryFileUrl={
                      advanceData.signing_document.signed_pdf_url && advanceData.signing_document.original_file_url
                        ? advanceData.signing_document.original_file_url
                        : undefined
                    }
                  />
                </div>
              </div>
            )}

          {advanceData.journal_entries && advanceData.journal_entries.length > 0 && (
            <div className="py-3 flex flex-col gap-2 border-t surface-base-stroke mt-2">
              <span className="text-body-regular-md content-base-secondary">{t("detail.journalEntriesTitle")}</span>
              {advanceData.journal_entries.map(function renderJournalEntry(entry) {
                return (
                  <div key={entry.id} className="flex flex-col gap-1 p-3 radius-md surface-secondary-fill">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-body-regular-sm content-base-secondary">{formatDateForDisplay(entry.entry_date)}</span>
                      <span className="text-body-bold-sm content-base-primary">{formatMoneyKzt(entry.amount)}</span>
                    </div>
                    <p className="text-body-regular-sm content-base-secondary">
                      {t("detail.journalEntryDebit")}: {entry.debit_account} → {t("detail.journalEntryCredit")}: {entry.credit_account}
                    </p>
                    <p className="text-body-regular-sm content-base-low">{entry.status}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderTripContent() {
    if (!tripData) return null;

    const isSettled = tripData.status === "settled";
    const reportLines = tripData.report_lines ?? [];
    const reportLinesTotal = reportLines.reduce((sum, line) => sum + (Number(line.gross_amount) || 0), 0);
    const totalAmount =
      reportLines.length > 0
        ? String(reportLinesTotal)
        : tripData.amount_issued && Number(tripData.amount_issued) > 0
          ? tripData.amount_issued
          : String((Number(tripData.per_diem_total) || 0) + (Number(tripData.advance_amount) || 0));

    const fields: FieldConfig[] = [
      {
        key: "worker",
        label: t("detail.worker"),
        value: tripData.worker_name,
        icon: Profile as typeof DocumentText1,
      },
      {
        key: "destination",
        label: t("detail.destination"),
        value: tripData.destination,
        icon: Location as typeof DocumentText1,
      },
      {
        key: "reason",
        label: t("detail.businessTripReason"),
        value: tripData.business_trip_reason ?? "",
        icon: DocumentText1,
      },
      {
        key: "tripPeriod",
        label: t("detail.tripPeriod"),
        value: t("detail.tripPeriodValue", {
          start: formatDateForDisplay(tripData.start_date),
          end: formatDateForDisplay(tripData.end_date),
          days: tripData.days,
        }),
        icon: Calendar as typeof DocumentText1,
      },
    ];

    if (isSettled) {
      fields.push({
        key: "amount",
        label: t("detail.amount"),
        value: formatMoneyKzt(totalAmount),
        icon: Moneys as typeof DocumentText1,
      });
      if (tripData.advance_amount != null && Number(tripData.advance_amount) > 0) {
        fields.push({
          key: "advanceLine",
          label: t("settlement.advanceLine"),
          value: formatMoneyKzt(tripData.advance_amount),
          icon: Moneys as typeof DocumentText1,
        });
      }
    } else {
      fields.push({
        key: "perDiemTotal",
        label: t("detail.perDiemTotal"),
        value: formatMoneyKzt(tripData.per_diem_total),
        icon: Moneys as typeof DocumentText1,
      });
      fields.push({
        key: "advanceAmount",
        label: t("detail.amount"),
        value: formatMoneyKzt(tripData.advance_amount),
        icon: Moneys as typeof DocumentText1,
      });
    }

    if (tripData.application_review_note) {
      fields.push({
        key: "revisionNote",
        label: t("detail.revisionNote"),
        value: tripData.application_review_note,
        icon: Edit2 as typeof DocumentText1,
      });
    }

    if (tripData.report_date) {
      fields.push({
        key: "reportDate",
        label: t("detail.reportDate"),
        value: formatDateForDisplay(tripData.report_date),
        icon: Calendar as typeof DocumentText1,
      });
    }

    if (tripData.settled_by_name) {
      fields.push({
        key: "settledBy",
        label: t("detail.settledBy"),
        value: tripData.settled_by_name,
        icon: Profile as typeof DocumentText1,
      });
    }

    fields.push({
      key: "status",
      label: t("detail.status"),
      value: "",
      icon: DocumentText1,
      isStatus: true,
      statusComponent: getStatusBadge(tripData.status),
    });

    const visibleFields = fields.filter(filterField);

    return (
      <div className="flex flex-col gap-6 h-full min-h-0">
        <div className="flex flex-col gap-2 pb-5 border-b surface-base-stroke shrink-0">
          <p className="text-display-2xs content-base-primary">{t("detail.tripTitle")}</p>
        </div>
        <div className="flex-1 overflow-auto">
          <div className="flex flex-col pt-2">
            {visibleFields.map((field, index, array) => renderField(field, index, array))}
          </div>

          {tripData.order && (tripData.order.signed_pdf_url || tripData.order.preview_pdf_url) && (
            <div className="py-3 flex flex-col gap-3 border-t surface-base-stroke">
              <span className="text-body-regular-md content-base-secondary">{t("detail.orderFileLabel")}</span>
              <div className="p-1">
                <FileViewer
                  existingFileUrl={(tripData.order.signed_pdf_url || tripData.order.preview_pdf_url) as string}
                  secondaryFileUrl={
                    tripData.order.signed_pdf_url && tripData.order.preview_pdf_url
                      ? tripData.order.preview_pdf_url
                      : undefined
                  }
                />
              </div>
            </div>
          )}

          {tripData.report_lines && tripData.report_lines.length > 0 && (
            <ReportLinesSection lines={tripData.report_lines} />
          )}

          {tripData.journal_entries && tripData.journal_entries.length > 0 && (
            <div className="py-3 flex flex-col gap-2 border-t surface-base-stroke mt-2">
              <span className="text-body-regular-md content-base-secondary">{t("detail.journalEntriesTitle")}</span>
              {tripData.journal_entries.map(function renderJournalEntry(entry) {
                return (
                  <div key={entry.id} className="flex flex-col gap-1 p-3 radius-md surface-secondary-fill">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-body-regular-sm content-base-secondary">{formatDateForDisplay(entry.entry_date)}</span>
                      <span className="text-body-bold-sm content-base-primary">{formatMoneyKzt(entry.amount)}</span>
                    </div>
                    <p className="text-body-regular-sm content-base-secondary">
                      {t("detail.journalEntryDebit")}: {entry.debit_account} → {t("detail.journalEntryCredit")}: {entry.credit_account}
                    </p>
                    <p className="text-body-regular-sm content-base-low">{entry.status}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderSkeleton() {
    return (
      <>
        <div className="flex flex-col gap-2 pb-5 border-b surface-base-stroke shrink-0">
          <Skeleton height={28} width={240} />
        </div>
        <div className="flex-1">
          <div className="flex flex-col py-4">
            <div className="flex flex-col gap-2 mb-4 border-b surface-base-stroke pb-3">
              <Skeleton height={24} width={200} />
            </div>
            <div className="flex flex-col gap-3">
              {Array.from({ length: 6 }).map(function renderSkeletonRow(_, idx) {
                return (
                  <div key={idx} className="py-3 border-b surface-base-stroke">
                    <Skeleton height={20} width="100%" />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <Skeleton height={44} width="100%" />
      </>
    );
  }

  return (
    <>
      <ModalForm icon={DocumentText1} onClose={onClose} resize={false} hidden={showSettlement} footer={renderFooter()}>
        <div className="flex flex-col gap-6 h-full min-h-0">
          {isLoading ? (
            renderSkeleton()
          ) : isError || (!advanceData && !tripData) ? (
            <p className="text-body-regular-md content-action-negative">{t("detail.loadError")}</p>
          ) : entityType === "advance" ? (
            renderAdvanceContent()
          ) : (
            renderTripContent()
          )}
        </div>
      </ModalForm>

      {confirmIssue && (
        <PromptForm
          variant="success"
          title={t("actions.issueConfirmTitle")}
          text={getIssueConfirmText()}
          confirmText={t("actions.issueConfirm")}
          cancelText={t("actions.cancel")}
          isLoading={issuingLocal}
          onConfirm={() => handleIssueConfirm()}
          onClose={() => setConfirmIssue(false)}
          namespace="AdvancesPage"
        />
      )}

      {showSalaryIssueModal && entityType === "advance" && advanceData && (
        <SalaryAdvanceIssueModal
          workerId={advanceData.worker_id}
          totalAmount={parseFloat(advanceData.amount)}
          fromCashDesk={fromCashDesk}
          onClose={() => setShowSalaryIssueModal(false)}
          onConfirm={handleIssueConfirm}
          isSubmitting={issuingLocal}
        />
      )}

      {showRevisionNoteModal && (
        <ReviewNoteModal
          action="revision"
          onClose={() => setShowRevisionNoteModal(false)}
          onSubmit={handleRevisionWithNote}
          isSubmitting={isReviewing}
          namespace="AdvancesPage"
          translationPrefix="review"
        />
      )}

      {showRejectNoteModal && (
        <ReviewNoteModal
          action="reject"
          onClose={() => setShowRejectNoteModal(false)}
          onSubmit={handleRejectWithNote}
          isSubmitting={isReviewing}
          namespace="AdvancesPage"
          translationPrefix="review"
        />
      )}

      {issuingLocal && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={t("messages.loading")}
          additionalText={t("messages.pleaseWait")}
          namespace="AdvancesPage"
        />
      )}

      {isDownloading && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={t("messages.downloadingT3")}
          additionalText={t("messages.pleaseWait")}
          namespace="AdvancesPage"
        />
      )}

      {tripSigning.isLoading && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={tCommon(`signing.phases.${tripSigning.phase}`)}
          additionalText={tCommon("signing.phases.pleaseWait")}
          namespace="Common"
        />
      )}

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant || "success"}
          onClose={handlePromptClose}
          namespace="AdvancesPage"
        />
      )}

      {showOrgSign && advanceData?.signing_document?.document_id && (
        <Suspense fallback={null}>
          <OrgSignByDocumentModal
            documentId={advanceData.signing_document.document_id}
            bytesUrl={advanceData.signing_document.bytes_url}
            workerName={advanceData.worker_name}
            documentType={t("detail.title")}
            advanceId={advanceData.id}
            onClose={() => setShowOrgSign(false)}
            onSuccess={() => {
              setShowOrgSign(false);
              onActionSuccess?.();
            }}
          />
        </Suspense>
      )}

      {showSettlement && entityType === "advance" && advanceData && (
        <AdvanceSettlementForm
          advanceId={advanceData.id}
          advanceAmount={advanceData.amount}
          onClose={() => setShowSettlement(false)}
          onSuccess={() => {
            setShowSettlement(false);
            onActionSuccess?.();
          }}
        />
      )}

      {showSettlement && entityType === "trip" && tripData && (
        <TripSettlementForm
          tripId={tripData.id}
          amountIssued={tripData.amount_issued ?? tripData.advance_amount}
          perDiemRate={tripData.per_diem_rate}
          defaultDays={tripData.days}
          onClose={() => setShowSettlement(false)}
          onSuccess={() => {
            setShowSettlement(false);
            onActionSuccess?.();
          }}
        />
      )}
    </>
  );
}
